import { RegionSwitcher } from './RegionSwitcher';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { LoadingState, ErrorState } from '../../components/states';
import { useMyClub, useRegion } from '../../hooks/queries';
import { useRuntime } from '../../hooks/useRuntime';
import { useTracking } from '../../services/runtime';
import { useAuth } from '../../stores/auth';
import { useMapTheme } from '../../stores/map-theme';
import { TerritorySheet } from '../territory/TerritorySheet';
import { GameMap } from './GameMap';
import { centerOn, type MapHandle } from './map-lib';
import { useOwnedTerritories } from './useOwnedTerritories';
import { geolocationPermission } from '../running/engine/geo-source';
import { toast } from '../../stores/toasts';
import { useT } from '../../i18n';
import { trackAd, useSponsoredCells } from '../campaigns/campaigns-api';
import type { SponsoredCell } from './GameMap';

export function MapPage() {
  const { region, runtime } = useRuntime();
  const regions = useRegion();
  const me = useAuth((s) => s.user?.id ?? null);
  const { owners } = useOwnedTerritories(region);
  const position = useTracking((s) => s.position);
  const route = useTracking((s) => s.route);
  const routeVersion = useTracking((s) => s.routeVersion);
  const captured = useTracking((s) => s.capturedCells);
  // Links from Rivals: ?lost=k1,k2 marks sectors you lost in red, ?focus=k opens one.
  const [params, setParams] = useSearchParams();
  const lost = useMemo(() => (params.get('lost') ?? '').split(',').filter(Boolean), [params]);
  const focus = params.get('focus');
  const [selected, setSelected] = useState<string | null>(focus);
  const [handle, setHandle] = useState<MapHandle | null>(null);
  const [viewMode, setViewMode] = useState<'runners' | 'clubs'>('runners');
  const mapTheme = useMapTheme((s) => s.theme);
  const toggleTheme = useMapTheme((s) => s.toggle);
  const myClub = useMyClub();
  const myClubId = myClub.data?.club?.id ?? null;
  const t = useT();
  const sponsors = useSponsoredCells(region?.dto.id);
  const sponsored = useMemo<SponsoredCell[]>(
    () => [...sponsors.byCell].map(([gridKey, c]) => ({ gridKey, color: c.brand.color, brandId: c.brand.id, logoUrl: c.brand.logoUrl })),
    [sponsors.byCell],
  );
  const byCell = sponsors.byCell;
  const onSponsoredVisible = useMemo(
    () => (keys: string[]) => {
      for (const id of new Set(keys.map((k) => byCell.get(k)?.id))) if (id) trackAd(id, 'CELL', 'VIEW');
    },
    [byCell],
  );
  const select = (key: string | null) => {
    const c = key ? byCell.get(key) : undefined;
    if (c) trackAd(c.id, 'CELL', 'CLICK');
    setSelected(key);
  };

  // Open where the action is: the player, else their territory, else contested territory.
  const initialCenter = useMemo(() => {
    const list = [...owners.values()];
    const pick = list.filter((t) => t.owner?.id === me).length ? list.filter((t) => t.owner?.id === me) : list;
    if (!region || pick.length === 0) return null;
    const pts = pick.map((t) => region.grid.centerOf(t.gridKey));
    return { lat: pts.reduce((a, p) => a + p.lat, 0) / pts.length, lng: pts.reduce((a, p) => a + p.lng, 0) / pts.length };
  }, [owners, me, region]);

  // GPS turned on by "locate me" stops when leaving the map (unless a run is recording).
  useEffect(() => () => runtime?.engine.coolDown(), [runtime]);

  // A linked sector wins over the player's position.
  const target = useMemo(() => {
    const keys = focus ? [focus] : lost;
    if (!region || !keys.length) return null;
    const pts = keys.map((k) => region.grid.centerOf(k));
    return { lat: pts.reduce((a, p) => a + p.lat, 0) / pts.length, lng: pts.reduce((a, p) => a + p.lng, 0) / pts.length };
  }, [focus, lost, region]);
  useEffect(() => {
    if (handle && target) centerOn(handle, target.lat, target.lng, focus ? 15.5 : 14.5);
  }, [handle, target, focus]);
  useEffect(() => setSelected(focus), [focus]);

  // Jump to the player once, the first time a GPS fix arrives.
  const centeredOnce = useRef(!!target);
  useEffect(() => {
    if (handle && position && !centeredOnce.current) {
      centeredOnce.current = true;
      centerOn(handle, position.lat, position.lng, 15);
    }
  }, [handle, position]);
  const centeredOnTerritory = useRef(!!target);
  useEffect(() => {
    if (handle && initialCenter && !position && !centeredOnTerritory.current) {
      centeredOnTerritory.current = true;
      centerOn(handle, initialCenter.lat, initialCenter.lng, 14);
    }
  }, [handle, initialCenter, position]);

  if (!region) {
    return regions.isError ? <ErrorState error={regions.error} onRetry={() => regions.refetch()} /> : <LoadingState label={t('Loading game region…')} />;
  }

  let mine = 0;
  let enemy = 0;
  for (const t of owners.values()) {
    if (!t.owner) continue;
    const ours = viewMode === 'clubs' ? !!myClubId && t.owner.club?.id === myClubId : t.owner.id === me;
    if (ours) mine++;
    else enemy++;
  }

  async function locate() {
    if ((await geolocationPermission()) === 'denied') {
      toast({ kind: 'error', title: t('Location blocked'), body: t('Allow location for this site in your browser settings.') });
      return;
    }
    runtime?.engine.warmUp();
    const p = runtime?.engine.snapshot.position;
    if (p) centerOn(handle, p.lat, p.lng, 15);
    else toast({ kind: 'info', title: t('Finding you…'), body: t('Waiting for GPS.') });
  }

  return (
    <div className="fixed inset-0 bottom-[calc(var(--nav-h)+var(--safe-bottom))]">
      <GameMap
        key={region.dto.id}
        className="absolute inset-0"
        region={region}
        owners={owners}
        myUserId={me}
        position={position}
        route={route}
        routeVersion={routeVersion}
        highlight={captured}
        lost={lost}
        selected={selected}
        onSelect={select}
        sponsored={sponsored}
        onSponsoredVisible={onSponsoredVisible}
        initialZoom={13.5}
        initialCenter={initialCenter}
        onReady={setHandle}
        viewMode={viewMode}
        myClubId={myClubId}
      />
      <div className="pointer-events-none absolute inset-x-0 top-0 p-3 pt-[calc(var(--safe-top)+12px)]">
        <div className="pointer-events-auto mx-auto flex max-w-lg items-center justify-between gap-2 rounded-2xl border border-ink-700 bg-ink-900/90 px-4 py-2.5 backdrop-blur">
          <div>
            <RegionSwitcher className="font-display text-lg font-bold uppercase leading-none" />
            <div className="text-[11px] text-ink-400">{t('{n} sectors', { n: region.dto.territoryCount.toLocaleString() })}</div>
          </div>
          <div className="flex gap-3 text-sm font-semibold">
            <Legend color="bg-volt" label={`${mine}`} title={t(viewMode === 'clubs' ? 'Club' : 'Yours')} />
            <Legend color="bg-enemy" label={`${enemy}`} title={t('Rivals')} />
          </div>
        </div>
        <div className="pointer-events-auto mx-auto mt-2 flex w-fit rounded-full border border-ink-700 bg-ink-900/90 p-1 text-xs font-bold uppercase backdrop-blur">
          {(['runners', 'clubs'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setViewMode(m)}
              data-testid={`map-view-${m}`}
              className={`rounded-full px-4 py-1.5 ${viewMode === m ? 'bg-volt text-ink-950' : 'text-ink-300'}`}
            >
              {t(m === 'runners' ? 'Runners' : 'Clubs')}
            </button>
          ))}
        </div>
        {lost.length ? (
          <div
            className="pointer-events-auto mx-auto mt-2 flex w-fit items-center gap-2 rounded-full border border-enemy/50 bg-ink-900/90 py-1 pl-3 pr-1 text-xs font-bold text-enemy backdrop-blur"
            data-testid="map-lost-chip"
          >
            {t(lost.length === 1 ? '1 sector to take back' : '{n} sectors to take back', { n: lost.length })}
            <button
              onClick={() => setParams({}, { replace: true })}
              className="grid size-6 place-items-center rounded-full bg-ink-800 text-ink-200"
              aria-label={t('Clear')}
            >
              ✕
            </button>
          </div>
        ) : null}
      </div>
      <button
        onClick={toggleTheme}
        className="absolute bottom-56 right-3 grid size-12 place-items-center rounded-full border border-ink-600 bg-ink-850 text-ink-100 shadow-lg"
        aria-label={t(mapTheme === 'light' ? 'Dark map' : 'Light map')}
        title={t(mapTheme === 'light' ? 'Dark map' : 'Light map')}
        data-testid="map-theme"
      >
        {mapTheme === 'light' ? (
          <svg className="size-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
            <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" />
          </svg>
        ) : (
          <svg className="size-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2m0 16v2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M2 12h2m16 0h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </svg>
        )}
      </button>
      <button
        onClick={locate}
        className="absolute bottom-40 right-3 grid size-12 place-items-center rounded-full border border-ink-600 bg-ink-850 text-volt shadow-lg"
        aria-label={t('Center on my location')}
        data-testid="locate-me"
      >
        <svg className="size-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v3m0 14v3M2 12h3m14 0h3" strokeLinecap="round" />
        </svg>
      </button>
      <TerritorySheet regionId={region.dto.id} gridKey={selected} sponsor={selected ? byCell.get(selected) : undefined} onClose={() => setSelected(null)} />
    </div>
  );
}

function Legend({ color, label, title }: { color: string; label: string; title: string }) {
  return (
    <span className="flex items-center gap-1.5" title={title}>
      <span className={`hex-clip size-3.5 ${color}`} />
      <span className="tabular">{label}</span>
      <span className="text-xs font-normal text-ink-400">{title}</span>
    </span>
  );
}
